import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { cn, formatDate } from '../../lib/utils';
import { 
  Building2, 
  Users, 
  CalendarCheck, 
  DollarSign,
  TrendingUp,
  ChevronRight,
  Activity
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const stats = data?.overview || {};

  const statCards = [
    {
      id: 'active-restaurants',
      label: 'Restaurantes Activos',
      value: stats.active_restaurants || 0,
      total: stats.total_restaurants || 0,
      icon: Building2,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
    {
      id: 'total-users',
      label: 'Total Usuarios',
      value: stats.total_users || 0,
      icon: Users,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
    },
    {
      id: 'today-reservations',
      label: 'Reservaciones Hoy',
      value: stats.today_reservations || 0,
      total: stats.total_reservations || 0,
      icon: CalendarCheck,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
    },
    {
      id: 'total-revenue',
      label: 'Revenue Total',
      value: `$${(stats.total_revenue || 0).toLocaleString()}`,
      icon: DollarSign,
      color: 'text-amber-500',
      bgColor: 'bg-amber-500/10',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-30 h-14 glass flex items-center justify-between px-6">
        <h1 className="text-lg font-semibold">Dashboard Super Admin</h1>
        <Badge variant="outline" className="bg-primary/10 text-primary">
          <Activity className="h-3 w-3 mr-1" /> Sistema Activo
        </Badge>
      </header>

      <div className="p-6 space-y-6 fade-in">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat) => (
            <Card key={stat.id} className="hover-lift" data-testid={`admin-stat-${stat.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">{stat.label}</p>
                    <p className="text-2xl font-bold mt-1">{stat.value}</p>
                    {stat.total !== undefined && (
                      <p className="text-xs text-muted-foreground mt-0.5">de {stat.total} total</p>
                    )}
                  </div>
                  <div className={cn("p-3 rounded-lg", stat.bgColor)}>
                    <stat.icon className={cn("h-5 w-5", stat.color)} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Restaurantes con Stripe</p>
                  <p className="text-xl font-bold">{stats.restaurants_with_stripe || 0}</p>
                </div>
                <TrendingUp className="h-5 w-5 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Comensales</p>
                  <p className="text-xl font-bold">{(stats.total_covers || 0).toLocaleString()}</p>
                </div>
                <Users className="h-5 w-5 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Reservaciones</p>
                  <p className="text-xl font-bold">{(stats.total_reservations || 0).toLocaleString()}</p>
                </div>
                <CalendarCheck className="h-5 w-5 text-primary" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <Card data-testid="recent-activity-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium">Reservaciones Recientes</CardTitle>
              <Link to="/admin/restaurants">
                <Button variant="ghost" size="sm">
                  Ver restaurantes <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {(data?.recent_reservations || []).length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No hay actividad reciente</p>
            ) : (
              (data?.recent_reservations || []).slice(0, 5).map((res) => (
                <div
                  key={res.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                >
                  <div>
                    <p className="font-medium text-sm">{res.guest_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {res.date} {res.time} • {res.party_size} personas
                    </p>
                  </div>
                  <Badge variant="outline" className={`status-${res.status}`}>
                    {res.status}
                  </Badge>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
